import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, resolveImage } from "../data/content";

export default function StoryPage() {
  return (
    <main data-testid="story-page">
      <Seo
        title="Our Story | The Jamoon, Corbett"
        description="Discover the story behind The Jamoon, a secluded wilderness lodge beside the Ramganga in Corbett, shaped by nature, simplicity and meaningful disconnection."
        path="/story"
        image={IMG.heroPoster}
        imageAlt="Forest path leading to The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Our Story", path: "/story" }]}
      />
      <PageHero
        testid="story-hero"
        image={IMG.heroPoster}
        imageAlt="Forest path leading to The Jamoon in Corbett"
        eyebrow="Our Story"
        title={<>Our <span className="italic text-brass">Story</span></>}
        sub="In a constantly progressing world, a few places still exist that are wild and untouched — a reminder of ageless wonder and beauty."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">The Name</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
                Named after a tree, <span className="italic text-brass">and a village.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-ivory/75">
                Jamoon derives its name from the jamun — the Malabar plum, an evergreen tropical
                tree — and from the village it stands in, deep in the forest beside the Lohachaur
                area of Corbett.
              </p>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-ivory/55 sm:text-base">
                The lodge is inspired by local tradition and built on the principle of
                minimalism: a life closer to a Himalayan village than to a hotel. It sits on a
                small hillock with a view over sal forest and the Ramganga — the river
                considered the lifeline of Corbett.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="group relative overflow-hidden">
            <img
              src={resolveImage("elephantSal", 900, 1100)}
              alt="An elephant among the sal trees around The Jamoon"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
          </Reveal>
        </div>
      </section>

      <section className="border-y border-brass/15 bg-forest px-6 py-24 sm:px-12 sm:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">The Jamoon Life</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-serif text-2xl font-light leading-relaxed text-ivory sm:text-3xl">
              "There is life — and then there is something called{" "}
              <span className="italic text-brass">the Jamoon life.</span>"
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-8 max-w-2xl text-sm leading-relaxed text-ivory/65 sm:text-base">
              The Jamoon hosts wildlife photographers, artists, authors and writers, adventure
              seekers, and anyone who wants to switch off. If you love hiking, the forest trails
              begin at the lodge. If you love wildlife, document it on foot, with camera traps,
              or from the hide. If you create, this is your reason to disconnect and begin.
            </p>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
